import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const DEFAULT_PROFILE_CURRENCY = 'INR';
const SUPPORTED_PROFILE_CURRENCIES = ['INR', 'USD'] as const;

interface BillingModeResponse {
  mode: 'payg' | 'subscription';
  productEnabled: boolean;
  assignedPlan: {
    id: string;
    name: string;
    priceMonthly: number;
    status: 'active' | 'archived';
  } | null;
  paygEstimate: {
    currency: string;
    total: number;
  };
}

interface CurrencyUpdateResponse {
  currency: string;
}

async function parseCurrencyUpdateResponse(response: Response): Promise<{ data: CurrencyUpdateResponse | null; error: string | null }> {
  const rawText = await response.text();
  if (!rawText) {
    return { data: null, error: null };
  }

  try {
    const parsed = JSON.parse(rawText) as { currency?: string; error?: string };
    if (typeof parsed.currency === 'string') {
      return { data: { currency: parsed.currency }, error: null };
    }
    if (typeof parsed.error === 'string') {
      return { data: null, error: parsed.error };
    }
    return { data: null, error: null };
  } catch {
    return { data: null, error: rawText.slice(0, 200) };
  }
}

function formatMoney(value: number, currency = DEFAULT_PROFILE_CURRENCY): string {
  const normalizedCurrency = typeof currency === 'string' && currency.trim()
    ? currency.trim().toUpperCase()
    : DEFAULT_PROFILE_CURRENCY;

  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: normalizedCurrency,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: DEFAULT_PROFILE_CURRENCY,
      maximumFractionDigits: 2,
    }).format(value);
  }
}

const ProfilePage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [imageError, setImageError] = useState(false);
  const [billingState, setBillingState] = useState<BillingModeResponse | null>(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<string>(DEFAULT_PROFILE_CURRENCY);
  const [currencySaving, setCurrencySaving] = useState(false);
  const [currencyError, setCurrencyError] = useState<string | null>(null);
  const [currencyNotice, setCurrencyNotice] = useState<string | null>(null);

  const fetchBilling = useCallback(async () => {
    setBillingLoading(true);
    setBillingError(null);

    try {
      const response = await fetch(`${API_BASE}/api/me/pricing`, { credentials: 'include' });
      if (!response.ok) {
        throw new Error('Unable to load subscription details right now.');
      }
      const data = await response.json() as BillingModeResponse;
      setBillingState(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load subscription details right now.';
      setBillingError(message);
    } finally {
      setBillingLoading(false);
    }
  }, []);

  useEffect(() => {
    setImageError(false);
  }, [user?.avatarUrl]);

  useEffect(() => {
    void fetchBilling();
  }, [fetchBilling]);

  useEffect(() => {
    if (!user?.billingCurrency) return;
    setSelectedCurrency(user.billingCurrency.toUpperCase());
  }, [user?.billingCurrency]);

  const handleSaveCurrency = async () => {
    setCurrencySaving(true);
    setCurrencyError(null);
    setCurrencyNotice(null);

    try {
      const normalizedCurrency = selectedCurrency.trim().toUpperCase();
      const methods: Array<'PATCH' | 'POST'> = ['PATCH', 'POST'];

      let data: CurrencyUpdateResponse | null = null;
      let lastError = 'Unable to update currency preference.';

      for (const method of methods) {
        const response = await fetch(`${API_BASE}/api/me/currency`, {
          method,
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ currency: normalizedCurrency }),
        });

        const parsed = await parseCurrencyUpdateResponse(response);
        if (response.ok && parsed.data) {
          data = parsed.data;
          break;
        }

        const statusError = parsed.error || `Request failed (${response.status})`;
        lastError = statusError;

        // Some environments reject PATCH/PUT; try POST fallback for method errors.
        if ((response.status === 404 || response.status === 405 || response.status === 501) && method !== methods[methods.length - 1]) {
          continue;
        }
      }

      if (!data) {
        throw new Error(lastError);
      }

      setSelectedCurrency((data.currency || selectedCurrency).toUpperCase());
      setCurrencyNotice('Billing currency updated successfully.');
      await refreshUser();
      await fetchBilling();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to update currency preference.';
      setCurrencyError(message);
    } finally {
      setCurrencySaving(false);
    }
  };

  const initials = useMemo(() => {
    if (!user?.name) return '??';
    return user.name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }, [user?.name]);

  const avatarUrl = useMemo(() => {
    if (!user?.avatarUrl) return null;
    return user.avatarUrl.startsWith('http') ? user.avatarUrl : `${API_BASE}${user.avatarUrl}`;
  }, [user?.avatarUrl]);

  const showAvatar = avatarUrl && !imageError;
  const subscriptionModeLabel = billingState?.mode === 'subscription' ? 'Subscription' : 'PAYG';
  const subscriptionPlanLabel = billingState?.assignedPlan?.name || user?.plan || 'PAYG';
  const subscriptionStatusLabel = billingState?.productEnabled === false ? 'Disabled' : 'Active';
  const currencyLocked = Boolean(user?.billingCurrencyLocked);
  const effectiveCurrency = (selectedCurrency || user?.billingCurrency || DEFAULT_PROFILE_CURRENCY).toUpperCase();

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="main-content">
        <Header />

        <div className="page-header">
          <div className="page-header-left">
            <h2>Profile</h2>
          </div>
          <Link to="/account/billing" className="header-action-btn header-action-primary">
            Manage Subscription
          </Link>
        </div>

        <p className="page-subtitle profile-page-subtitle">
          Manage your profile information and review your current subscription state.
        </p>

        <section className="profile-grid">
          <article className="profile-card">
            <h3>Personal Info</h3>
            <div className="profile-identity-row">
              {showAvatar ? (
                <img
                  src={avatarUrl}
                  alt={user?.name || 'User'}
                  className="profile-avatar-lg"
                  onError={() => setImageError(true)}
                />
              ) : (
                <span className="profile-avatar-lg profile-avatar-fallback">{initials}</span>
              )}

              <div className="profile-identity-copy">
                <p className="profile-name">{user?.name || 'User'}</p>
                <p className="profile-email">{user?.email || 'No email available'}</p>
              </div>
            </div>

            <div className="profile-meta-grid">
              <div className="profile-meta-cell">
                <span className="profile-meta-label">Role</span>
                <strong className="profile-meta-value">{user?.role || 'user'}</strong>
              </div>
              <div className="profile-meta-cell">
                <span className="profile-meta-label">Plan</span>
                <strong className="profile-meta-value">{subscriptionPlanLabel}</strong>
              </div>
              <div className="profile-meta-cell">
                <span className="profile-meta-label">Projects</span>
                <strong className="profile-meta-value">{user?.stats?.projectsCount ?? 0}</strong>
              </div>
              <div className="profile-meta-cell">
                <span className="profile-meta-label">Shorts Created</span>
                <strong className="profile-meta-value">{user?.stats?.shortsCreated ?? 0}</strong>
              </div>
            </div>
          </article>

          <article className="profile-card">
            <h3>Subscription</h3>

            {billingLoading ? (
              <p className="profile-muted">Loading subscription details...</p>
            ) : (
              <>
                <div className="profile-subscription-grid">
                  <div className="profile-subscription-cell">
                    <span className="profile-meta-label">Mode</span>
                    <strong className="profile-meta-value">{subscriptionModeLabel}</strong>
                  </div>
                  <div className="profile-subscription-cell">
                    <span className="profile-meta-label">Status</span>
                    <strong
                      className={`profile-status-pill ${subscriptionStatusLabel === 'Active' ? 'active' : 'disabled'}`}
                    >
                      {subscriptionStatusLabel}
                    </strong>
                  </div>
                  <div className="profile-subscription-cell">
                    <span className="profile-meta-label">Assigned Plan</span>
                    <strong className="profile-meta-value">{subscriptionPlanLabel}</strong>
                  </div>
                  <div className="profile-subscription-cell">
                    <span className="profile-meta-label">Est. PAYG</span>
                    <strong className="profile-meta-value">
                      {billingState ? formatMoney(billingState.paygEstimate.total, billingState.paygEstimate.currency || effectiveCurrency) : 'N/A'}
                    </strong>
                  </div>
                </div>

                <div className="profile-currency-control">
                  <span className="profile-meta-label">Billing Currency</span>
                  <div className="profile-currency-row">
                    <select
                      className="profile-currency-select"
                      value={effectiveCurrency}
                      onChange={(event) => setSelectedCurrency(event.target.value)}
                      disabled={currencySaving || currencyLocked}
                    >
                      {SUPPORTED_PROFILE_CURRENCIES.map((currency) => (
                        <option key={currency} value={currency}>{currency}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="profile-currency-save"
                      onClick={() => { void handleSaveCurrency(); }}
                      disabled={currencySaving || currencyLocked || effectiveCurrency === (user?.billingCurrency || DEFAULT_PROFILE_CURRENCY).toUpperCase()}
                    >
                      {currencySaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                  {currencyLocked && <p className="profile-inline-notice">Billing currency is locked after your first payment.</p>}
                  {currencyNotice && <p className="profile-inline-notice">{currencyNotice}</p>}
                  {currencyError && <p className="profile-inline-error">{currencyError}</p>}
                </div>

                {billingError && <p className="profile-inline-error">{billingError}</p>}
              </>
            )}

            <div className="profile-link-row">
              <Link to="/account/billing" className="profile-link-pill">Open Billing</Link>
              <Link to="/pricing" className="profile-link-pill">Compare Plans</Link>
            </div>
          </article>
        </section>

        <section className="profile-card profile-links-card">
          <h3>Quick Links</h3>
          <div className="profile-link-row">
            <Link to="/account" className="profile-link-pill">Social Accounts</Link>
            <Link to="/projects" className="profile-link-pill">Projects</Link>
            <Link to="/dashboard" className="profile-link-pill">Dashboard</Link>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ProfilePage;